import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { registerSignals } from "@/views/Risk/riskRegisterData";
import { getNextSequentialId } from "@/views/Risk/shared/utils";
import type { SignalTableRow } from "@/views/Risk/Signals/utils";

const STORAGE_KEY = "vivo_added_signals";
const HIDDEN_KEY = "vivo_hidden_signals";
const baseSignals = registerSignals;

function loadAdded(): SignalTableRow[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SignalTableRow[]) : [];
  } catch {
    return [];
  }
}

function saveAdded(added: SignalTableRow[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(added));
  } catch {
    /* storage unavailable */
  }
}

function loadHidden(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveHidden(hidden: Set<string>) {
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hidden]));
  } catch {
    /* storage unavailable */
  }
}

interface SignalsContextValue {
  signals: SignalTableRow[];
  addSignal: (partial: Partial<SignalTableRow>) => string;
  updateSignal: (signal_id: string, partial: Partial<SignalTableRow>) => void;
  removeSignal: (signal_id: string) => void;
}

const SignalsContext = createContext<SignalsContextValue>({
  signals: baseSignals,
  addSignal: () => "",
  updateSignal: () => {},
  removeSignal: () => {},
});

export function SignalsProvider({ children }: { children: ReactNode }) {
  const [added, setAdded] = useState<SignalTableRow[]>(loadAdded);
  const [hidden, setHidden] = useState<Set<string>>(loadHidden);

  const signals = [...added, ...baseSignals].filter(
    (s) => !hidden.has(s.signal_id as string),
  );

  const addSignal = useCallback((partial: Partial<SignalTableRow>): string => {
    let generatedId = "";
    setAdded((prev) => {
      const all = [...prev, ...baseSignals];
      const signal_id = getNextSequentialId({
        data: all,
        idField: "signal_id",
        prefix: "S",
      });
      generatedId = signal_id;
      const newRow: SignalTableRow = {
        signal: "",
        category: "",
        current_score: "",
        band: "Green",
        current_status: "G",
        breach_status: false,
        low_threshold: "",
        high_threshold: "",
        linked_risks: "",
        linked_breaches: "",
        owners: "",
        assignee: "",
        created_by: "",
        comments_governance_log: "",
        longitudinal_signal_data: "",
        longitudinal_signal_status: "",
        ...partial,
        signal_id,
      };
      const next = [newRow, ...prev];
      saveAdded(next);
      return next;
    });
    return generatedId;
  }, []);

  const updateSignal = useCallback(
    (signal_id: string, partial: Partial<SignalTableRow>) => {
      setAdded((prev) => {
        const next = prev.map((s) =>
          s.signal_id === signal_id ? { ...s, ...partial } : s,
        ) as SignalTableRow[];
        saveAdded(next);
        return next;
      });
    },
    [],
  );

  const removeSignal = useCallback(
    (signal_id: string) => {
      const isAdded = added.some((s) => s.signal_id === signal_id);
      if (isAdded) {
        setAdded((prev) => {
          const next = prev.filter((s) => s.signal_id !== signal_id);
          saveAdded(next);
          return next;
        });
      } else {
        setHidden((prev) => {
          const next = new Set(prev);
          next.add(signal_id);
          saveHidden(next);
          return next;
        });
      }
    },
    [added],
  );

  return (
    <SignalsContext.Provider
      value={{ signals, addSignal, updateSignal, removeSignal }}
    >
      {children}
    </SignalsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context file intentionally exports both provider and hook
export function useSignals() {
  return useContext(SignalsContext);
}
